import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import GiftItemPreview from '@service/giftStore/components/GiftItemPreview';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import { giftDeliveryCategoryListDataAtom } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { GiftDeliveryCategoryData } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.interface';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import { trackGiftEvent } from '@shared/api/giftEventLog/giftEventLog.controller';
import {
  giftItemListForStoreDataAtomFamily,
  giftItemListForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { useRedirect } from '@shared/hooks/useRedirect';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import _ from 'lodash';
import {
  Link,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';
import giftPromStoreMain from 'src/assets/gift/gift-prom-store-main.png';

import { GiftCallHelperBox } from '../components/GiftCallHelperBox';
import GiftStoreFooter from '../components/GiftStoreFooter';

type DeliverySection = {
  category: GiftDeliveryCategoryData;
  groups: { sub: string; items: GiftItemForStore[] }[];
  ungrouped: GiftItemForStore[];
  count: number;
};

// 상단 점프 내비에 항상 전부 노출되는 칩 (하위분류 또는 하위분류 없는 카테고리)
type DeliveryChip = {
  key: string; // `sub:<id>:<name>` | `cat:<id>` | `etc`
  label: string;
  count: number;
};

const GiftStoreMainPage: FC = function GiftStoreMainPage() {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');

  const method = searchParams.get('method');
  const fromAlimTalk = method === 'kakao';

  useRedirect({ skip: !fromAlimTalk });

  useEffect(() => {
    if (fromAlimTalk) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('method');
      setSearchParams(nextParams, { replace: true });
    }
  }, [fromAlimTalk, searchParams, setSearchParams]);

  const paramsString = searchParams.toString();

  useEffect(() => {
    if (_.isNull(bugoId)) {
      navigate('/');
    } else {
      trackGiftEvent('store_view', bugoId);
    }
  }, [bugoId, navigate]);

  useGiftItemListForStoreHook(bugoId ?? '');
  const status = useRecoilValue(giftItemListForStoreStatusAtomFamily(bugoId ?? ''));
  const data = useRecoilValue(giftItemListForStoreDataAtomFamily(bugoId ?? ''));

  useGiftDeliveryCategoryListHook(LowerRole.Public);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);
  const getImageUrl = (item: GiftItemForStore): string => {
    const image = item.image || item.giftItemInfo?.image;
    if (image && typeof image === 'object' && 'url' in image) {
      return fileBucketUrl((image as Image).url, 'w400');
    }
    return '';
  };

  const getName = (item: GiftItemForStore): string => {
    return item.name || item.giftItemInfo?.name || '';
  };

  const getType = (item: GiftItemForStore): GiftItemInfoType => {
    return item.giftItemInfo?.type || GiftItemInfoType.Delivery;
  };

  const getTags = (item: GiftItemForStore): string[] => {
    return item.giftItemInfo?.tags || [];
  };

  const getIsSelectable = (item: GiftItemForStore): boolean => {
    return item.giftItemInfo?.isSelectable || false;
  };

  const getChoiceUnitCount = (item: GiftItemForStore): number => {
    const units = item.giftItemInfo?.choiceUnits;
    if (!units || !Array.isArray(units)) return 0;
    return units.filter(
      (u): u is GiftChoiceUnitData => typeof u === 'object' && u !== null,
    ).length;
  };

  const getBrandName = (item: GiftItemForStore): string | undefined => {
    const brand = item.giftItemInfo?.giftItemBrand;
    if (brand && typeof brand === 'object' && 'name' in brand) {
      return brand.name;
    }
    return undefined;
  };

  const getDisplayOrder = (item: GiftItemForStore): number => {
    if (item.order !== undefined) return item.order;
    if (item.giftItemInfo?.order !== undefined) return item.giftItemInfo.order;
    return Number.MAX_SAFE_INTEGER;
  };

  const getCategoryId = (item: GiftItemForStore): string | undefined => {
    const override = item.giftDeliveryCategory;
    if (override) {
      return typeof override === 'object' ? override._id : override;
    }
    const fromInfo = item.giftItemInfo?.giftDeliveryCategory;
    if (fromInfo) {
      return typeof fromInfo === 'object' ? fromInfo._id : fromInfo;
    }
    return undefined;
  };

  // 상품이 선택한 하위분류 이름 (원상품에만 존재)
  const getSubCategory = (item: GiftItemForStore): string | undefined => {
    return item.giftItemInfo?.subCategory || undefined;
  };

  const sortedData = useMemo(() => {
    if (!data) return null;
    return [...data]
      .filter((item) => item.giftItemInfo?.isEnabled !== false)
      .sort((a, b) => {
        const typeA = getType(a) === GiftItemInfoType.Coupon ? 0 : 1;
        const typeB = getType(b) === GiftItemInfoType.Coupon ? 0 : 1;
        if (typeA !== typeB) return typeA - typeB;
        return getDisplayOrder(a) - getDisplayOrder(b);
      });
  }, [data]);

  // 상세 → 뒤로가기 시 보던 스크롤 위치 복원용 키
  const scrollStorageKey = `giftStoreScroll:${bugoId ?? ''}`;

  // 상세 페이지 등으로 떠날 때 현재 스크롤 위치를 저장한다.
  // (ScrollToTop이 POP에서 맨 위로 보내지 않으므로 복원 가능)
  useEffect(() => {
    return () => {
      sessionStorage.setItem(scrollStorageKey, String(window.scrollY));
    };
  }, [scrollStorageKey]);

  // 뒤로가기(POP)로 돌아와 목록이 준비되면 저장된 위치로 복원한다.
  useLayoutEffect(() => {
    if (navigationType !== 'POP') return;
    if (status !== 'success' || !sortedData) return;
    const saved = sessionStorage.getItem(scrollStorageKey);
    if (saved === null) return;
    window.scrollTo(0, Number(saved));
    sessionStorage.removeItem(scrollStorageKey);
  }, [navigationType, status, sortedData, scrollStorageKey]);

  // 배송형 탭으로 스크롤 이동시키기 위한 ref
  const typeTabRef = useRef<HTMLDivElement>(null);
  // 배송형 점프 타깃 ref. 키 형식: `cat:<id>` | `sub:<id>:<name>` | `etc`
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  // 하위분류 칩 줄(가로 스크롤 컨테이너)과 각 칩 버튼 ref — 활성 칩을 보이게 스크롤
  const chipScrollRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const scrollToTop = (el: HTMLElement | null) => {
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const scrollToTarget = (key: string) => {
    const el = sectionRefs.current[key];
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // 필터는 URL(tab)에 보존해 상세 → 뒤로가기 시 동일한 탭으로 복원한다.
  const [typeFilter, setTypeFilter] = useState<'Coupon' | 'Delivery'>(() =>
    searchParams.get('tab') === 'Delivery' ? 'Delivery' : 'Coupon',
  );

  const changeTypeFilter = (next: 'Coupon' | 'Delivery') => {
    setTypeFilter(next);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', next);
    setSearchParams(nextParams, { replace: true });
  };

  // 점프 내비에서 현재 보고 있는 섹션 키 (스크롤 추적 하이라이트용).
  // 키 형식: `cat:<id>` | `sub:<id>:<name>` | `etc`
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const typeCounts = useMemo(() => {
    if (!sortedData) return { Coupon: 0, Delivery: 0 };
    return {
      Coupon: sortedData.filter((item) => getType(item) === GiftItemInfoType.Coupon)
        .length,
      Delivery: sortedData.filter((item) => getType(item) === GiftItemInfoType.Delivery)
        .length,
    };
  }, [sortedData]);

  const hasBothTypes = typeCounts.Coupon > 0 && typeCounts.Delivery > 0;

  const isDeliveryViewActive = hasBothTypes
    ? typeFilter === 'Delivery'
    : typeCounts.Delivery > 0 && typeCounts.Coupon === 0;

  // 쿠폰형(또는 단일 타입) 그리드용 데이터
  const couponData = useMemo(() => {
    if (!sortedData) return [];
    if (!hasBothTypes) return sortedData;
    return sortedData.filter((item) => getType(item) === GiftItemInfoType.Coupon);
  }, [sortedData, hasBothTypes]);

  // 배송형 상품이 실제로 존재하는 카테고리 id 집합
  const usedCategoryIds = useMemo(() => {
    const ids = new Set<string>();
    (sortedData ?? []).forEach((item) => {
      if (getType(item) !== GiftItemInfoType.Delivery) return;
      const id = getCategoryId(item);
      if (id) ids.add(id);
    });
    return ids;
  }, [sortedData]);

  // 배송형 상품이 있는 카테고리만 (order 정렬은 fetch 시점에 적용됨)
  const categoryOptions = useMemo(() => {
    return deliveryCategoryList.filter((c) => usedCategoryIds.has(c._id));
  }, [deliveryCategoryList, usedCategoryIds]);

  // 배송형: 카테고리 → 하위분류 섹션 그룹.
  // 필터로 걸러내지 않고 전 상품을 섹션으로 펼쳐서 한 스크롤에 노출한다.
  const deliveryGrouping = useMemo(() => {
    const empty = {
      sections: [] as DeliverySection[],
      uncategorized: [] as GiftItemForStore[],
      chips: [] as DeliveryChip[],
    };
    if (!sortedData) return empty;
    const deliveryItems = sortedData.filter(
      (item) => getType(item) === GiftItemInfoType.Delivery,
    );
    const coveredIds = new Set(categoryOptions.map((c) => c._id));
    const sections: DeliverySection[] = categoryOptions.map((category) => {
      const items = deliveryItems.filter(
        (item) => getCategoryId(item) === category._id,
      );
      const subNames = category.subCategories ?? [];
      const usedSubs = subNames.filter((name) =>
        items.some((item) => getSubCategory(item) === name),
      );
      const groups = usedSubs.map((sub) => ({
        sub,
        items: items.filter((item) => getSubCategory(item) === sub),
      }));
      // 하위분류 미지정이거나 카테고리에 없는 하위분류 상품
      const ungrouped = items.filter((item) => {
        const sub = getSubCategory(item);
        return !sub || !usedSubs.includes(sub);
      });
      return { category, groups, ungrouped, count: items.length };
    });
    // 카테고리가 없거나 노출 카테고리에 속하지 않는 배송 상품 (누락 방지)
    const uncategorized = deliveryItems.filter((item) => {
      const id = getCategoryId(item);
      return !id || !coveredIds.has(id);
    });

    // 점프 내비 칩: 모든 하위분류를 펼쳐서 한 줄에 노출.
    // 하위분류가 없는 카테고리는 카테고리명 칩으로, 미분류는 '기타' 칩으로.
    const chips: DeliveryChip[] = [];
    sections.forEach(({ category, groups, count }) => {
      if (groups.length > 0) {
        groups.forEach(({ sub, items }) =>
          chips.push({
            key: `sub:${category._id}:${sub}`,
            label: sub,
            count: items.length,
          }),
        );
      } else {
        chips.push({ key: `cat:${category._id}`, label: category.name, count });
      }
    });
    if (uncategorized.length > 0) {
      chips.push({ key: 'etc', label: '기타', count: uncategorized.length });
    }

    return { sections, uncategorized, chips };
  }, [sortedData, categoryOptions]);

  const deliverySections = deliveryGrouping.sections;
  const uncategorizedDelivery = deliveryGrouping.uncategorized;
  const deliveryChips = deliveryGrouping.chips;

  // 배송형 진입 시 첫 칩을 활성으로 초기화
  useEffect(() => {
    if (!isDeliveryViewActive) return;
    setActiveKey((prev) => prev ?? deliveryChips[0]?.key ?? null);
  }, [isDeliveryViewActive, deliveryChips]);

  // 스크롤에 따라 현재 보고 있는 섹션을 추적해 칩/카테고리 내비를 하이라이트
  useEffect(() => {
    if (!isDeliveryViewActive || deliveryChips.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const key = (visible[0]?.target as HTMLElement | undefined)?.dataset.targetKey;
        if (key) setActiveKey(key);
      },
      { rootMargin: '-72px 0px -55% 0px', threshold: 0 },
    );
    deliveryChips.forEach(({ key }) => {
      const el = sectionRefs.current[key];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isDeliveryViewActive, deliveryChips]);

  // 활성 칩이 속한 카테고리 id (원형 카테고리 내비 하이라이트용)
  const activeCategoryId = useMemo(() => {
    if (!activeKey) return null;
    if (activeKey.startsWith('cat:')) return activeKey.slice(4);
    if (activeKey.startsWith('sub:')) {
      const rest = activeKey.slice(4);
      const sep = rest.indexOf(':');
      return sep === -1 ? rest : rest.slice(0, sep);
    }
    return null;
  }, [activeKey]);

  // 활성 칩이 칩 줄 밖(오른쪽 등)에 있으면 줄을 가로 스크롤해 보이게 한다.
  useEffect(() => {
    if (!isDeliveryViewActive || !activeKey) return;
    const container = chipScrollRef.current;
    const el = chipRefs.current[activeKey];
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    if (eRect.left < cRect.left || eRect.right > cRect.right) {
      const delta = eRect.left - cRect.left - (container.clientWidth - el.clientWidth) / 2;
      container.scrollTo({ left: container.scrollLeft + delta, behavior: 'smooth' });
    }
  }, [activeKey, isDeliveryViewActive]);

  const renderGrid = (items: GiftItemForStore[], keyPrefix: string) => (
    <div className="grid grid-cols-2 gap-2.5">
      {items.map((item, idx) => (
        <Link
          key={`${keyPrefix}-${idx}`}
          to={`/gift-store/item/${item._id}?${paramsString}`}
          state={{ name: getName(item) }}
        >
          <GiftItemPreview
            image={getImageUrl(item)}
            name={getName(item)}
            priceRegular={item.priceRegular}
            priceRetail={item.priceRetail}
            type={getType(item)}
            tags={getTags(item)}
            isSelectable={getIsSelectable(item)}
            choiceUnitCount={getChoiceUnitCount(item)}
            brandName={getBrandName(item)}
          />
        </Link>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 히어로 배너 */}
      <div>
        <img src={giftPromStoreMain} alt="답례품 안내" className="w-full" />
      </div>

      {/* 상품 목록 */}
      {status === 'success' ? (
        sortedData && sortedData.length > 0 ? (
          <div className="px-3 pb-4 pt-3">
            {/* 타입 필터 탭 */}
            {hasBothTypes && (
              <div
                ref={typeTabRef}
                className="mb-3 scroll-mt-16 overflow-hidden rounded-full bg-gray-200 shadow-sm ring-1 ring-gray-200"
              >
                <div className="grid grid-cols-2 gap-px">
                  {[
                    { key: 'Coupon' as const, label: '쿠폰형' },
                    { key: 'Delivery' as const, label: '배송형' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        changeTypeFilter(tab.key);
                        // 배송형 전환 시 쿠폰형 배너를 지나 탭 바가 맨 위에 오도록 스크롤
                        if (tab.key === 'Delivery') scrollToTop(typeTabRef.current);
                      }}
                      className={`py-2.5 text-sm font-semibold transition-colors ${
                        typeFilter === tab.key
                          ? 'bg-gray-900 text-white'
                          : 'bg-white text-gray-500 active:bg-gray-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isDeliveryViewActive ? (
              <>
                {/* 카테고리 쇼케이스 (브랜드 이미지, 탭하면 섹션으로 점프) */}
                {deliverySections.length > 1 && (
                  <div className="-mx-3 mb-3">
                    <div className="overflow-x-auto hide-scrollbar">
                      <div className="flex min-w-full justify-evenly gap-4 px-3 pb-1 pt-1">
                        {deliverySections.map(({ category }) => {
                          const isActive = activeCategoryId === category._id;
                          const imageUrl =
                            category.image && typeof category.image === 'object'
                              ? fileBucketUrl(category.image.url, 'raw')
                              : '';
                          return (
                            <button
                              key={category._id}
                              onClick={() => scrollToTarget(`cat:${category._id}`)}
                              className="flex w-[4.5rem] shrink-0 flex-col items-center gap-1.5"
                            >
                              <div
                                className={`flex h-[4.5rem] w-[4.5rem] items-center justify-center overflow-hidden rounded-full bg-gray-100 transition-all ${
                                  isActive ? 'ring-2 ring-offset-2' : ''
                                }`}
                              >
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={category.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span className="h-full w-full bg-gray-100" />
                                )}
                              </div>
                              <span
                                className={`truncate text-13 font-semibold ${
                                  isActive ? 'theme-text-main' : 'text-gray-800'
                                }`}
                              >
                                {category.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* 하위분류 칩 (전부 노출, 스티키, 탭하면 해당 분류로 점프) */}
                {deliveryChips.length > 1 && (
                  <div className="sticky top-0 z-20 -mx-3 mb-4 border-b border-gray-100 bg-gray-50 px-3">
                    <div ref={chipScrollRef} className="overflow-x-auto hide-scrollbar">
                      <div className="flex min-w-full gap-2 py-2">
                        {deliveryChips.map((chip) => {
                          const isActive = activeKey === chip.key;
                          return (
                            <button
                              key={chip.key}
                              ref={(el) => {
                                chipRefs.current[chip.key] = el;
                              }}
                              onClick={() => {
                                setActiveKey(chip.key);
                                scrollToTarget(chip.key);
                              }}
                              className={`flex shrink-0 items-center gap-1 rounded-full px-3.5 py-1.5 text-13 font-semibold transition-colors ${
                                isActive
                                  ? 'theme-bg-main text-white'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {chip.label}
                              <span
                                className={`text-xs font-normal ${
                                  isActive ? 'text-white/80' : 'text-gray-400'
                                }`}
                              >
                                {chip.count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* 카테고리 → 하위분류 섹션 (전 상품을 한 스크롤에 노출) */}
                <div className="space-y-7">
                  {deliverySections.map(({ category, groups, ungrouped }) => (
                    <section
                      key={category._id}
                      data-target-key={`cat:${category._id}`}
                      ref={(el) => {
                        sectionRefs.current[`cat:${category._id}`] = el;
                      }}
                      className="scroll-mt-16"
                    >
                      <h2 className="mb-3 text-base font-bold text-gray-900">
                        {category.name}
                      </h2>
                      {ungrouped.length > 0 && (
                        <div className="mb-5">
                          {renderGrid(ungrouped, `cat-${category._id}-ungrouped`)}
                        </div>
                      )}
                      <div className="space-y-5">
                        {groups.map(({ sub, items }) => (
                          <div
                            key={sub}
                            data-target-key={`sub:${category._id}:${sub}`}
                            ref={(el) => {
                              sectionRefs.current[`sub:${category._id}:${sub}`] = el;
                            }}
                            className="scroll-mt-16"
                          >
                            <h3 className="mb-2.5 flex items-center gap-1.5 text-sm font-semibold text-gray-500">
                              {sub}
                              <span className="text-xs font-normal text-gray-400">
                                {items.length}
                              </span>
                            </h3>
                            {renderGrid(items, `sub-${category._id}-${sub}`)}
                          </div>
                        ))}
                      </div>
                    </section>
                  ))}

                  {/* 카테고리 미지정 배송 상품 (누락 방지) */}
                  {uncategorizedDelivery.length > 0 && (
                    <section
                      data-target-key="etc"
                      ref={(el) => {
                        sectionRefs.current.etc = el;
                      }}
                      className="scroll-mt-16"
                    >
                      {deliverySections.length > 0 && (
                        <h2 className="mb-3 text-base font-bold text-gray-900">기타</h2>
                      )}
                      {renderGrid(uncategorizedDelivery, 'delivery-etc')}
                    </section>
                  )}
                </div>
              </>
            ) : (
              /* 쿠폰형 (또는 단일 타입) 그리드 */
              renderGrid(couponData, 'coupon')
            )}
          </div>
        ) : (
          <div className="center-box h-screen-15 flex-col gap-2 text-gray-500">
            <p>등록된 답례품이 없습니다.</p>
          </div>
        )
      ) : (
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      )}
      <GiftCallHelperBox />
      <GiftStoreFooter />
    </div>
  );
};

export default GiftStoreMainPage;
